import queryString from 'qs';
import { useAppSelector, useHistory, useLocation } from 'src/utils/Hooks';
import { addPersistentQueryParams } from 'src/utils/browser';
import { FormData, getEmptyFormData, mapConditionsToQuery, prefillFormData } from 'src/utils/segments';
import _cloneDeep from 'lodash/cloneDeep';
import { getAppName, getQueryParamValue } from 'src/utils/app';

export enum FILTER_OPERATION {
  /**
   * Adds a new value to the filter
   */
  Add = 'add',
  /**
   * Clears the filter of all values for a given key
   */
  Clear = 'clear',
  /**
   * Toggles the filter by adding it if it doesn't exist, or removing it if it does exist
   */
  Toggle = 'toggle',
  /**
   * Deletes a specific value from the filter for a given key
   */
  Delete = 'delete',

  /**
   * Transfer values to different tab
   */
  Transfer = 'transfer',

  /**
   * Clears all filters
   */
  Clear_All = 'clearAll'
}

interface UpdateSavedSearchParams {
  /**
   * The key of the filter to update
   */
  key?: string;
  /**
   * The new value to add to the filter
   */
  newValue?: string;
  /**
   * The operation to perform on the filter. Options are Add, Clear, Toggle, Delete, Clear All
   */
  operation?: FILTER_OPERATION | null;
  /**
   * Callback function to update the filter. If provided, the key, newValue, and operation will be ignored.
   * Used for special cases when the filter needs to be updated in a specific way does that does not fit the usual operations.
   * @returns a copy of new form data
   */
  callback?: (formData: FormData) => FormData;

  transferKey?: string;
}

/**
 * Hook to build and update saved searches using the legacy Beacon logic.
 * This hook creates a simplified way to create segments and update them based on user interactions.
 * Filter state is stored in the URL and updated using the history API.
 * The form data is then parsed from the URL and used to derive the segment conditions.
 * For saving a segment, see SegmentBuilderSidebar.tsx
 */
export const useSavedSearchBuilder = () => {
  const history = useHistory();
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const retailer = useAppSelector((state) => state.retailer);
  const location = useLocation();
  const entityType = getQueryParamValue('entityType');
  const appName = getAppName();
  const params = queryString.parse(location.search, { ignoreQueryPrefix: true, arrayLimit: 10e8 });
  const formData = prefillFormData({ queryParams: params, appName });

  function updateFilterParams(form: FormData, create = true) {
    const newParams = mapConditionsToQuery(form);

    const searchPageBool = !!getQueryParamValue('searchPage');

    history.push(
      `/search?${addPersistentQueryParams(
        retailer,
        mainTimePeriod
      )}&doAggregation=${true}&entityType=${entityType}&${queryString.stringify(newParams)}&showPriceData=${
        newParams.showPriceData === '1' ? '1' : '0'
      }&segment=${create ? '&action=create' : ''}&businessUnits={}${searchPageBool ? '&searchPage=true' : ''}`
    );
  }

  const updateSavedSearch = ({ key, newValue, operation, callback, transferKey }: UpdateSavedSearchParams) => {
    let newFormData: FormData;

    if (callback) {
      newFormData = callback(_cloneDeep(formData));
      updateFilterParams(newFormData);
      return;
    }

    switch (operation) {
      case FILTER_OPERATION.Transfer:
        newFormData = {
          ...formData,
          termFilters: {
            ...formData.termFilters,
            [transferKey]: {
              condition: formData.termFilters[transferKey]?.condition,
              values: []
            },
            [key]: {
              condition: formData.termFilters[key]?.condition,
              values: [...(formData.termFilters[key]?.values ?? []), { i: newValue }]
            }
          }
        };
        // eslint-disable-next-line consistent-return
        return updateFilterParams(newFormData, false);
      case FILTER_OPERATION.Add:
        newFormData = {
          ...formData,
          termFilters: {
            ...formData.termFilters,
            [key]: {
              condition: formData.termFilters[key]?.condition,
              values: [...(formData.termFilters[key]?.values ?? []), { i: newValue }]
            }
          }
        };
        break;
      case FILTER_OPERATION.Clear:
        newFormData = {
          ...formData,
          termFilters: {
            ...formData.termFilters,
            [key]: {
              condition: formData.termFilters[key]?.condition,
              values: []
            }
          }
        };
        break;

      case FILTER_OPERATION.Toggle:
        newFormData = {
          ...formData,
          termFilters: {
            ...formData.termFilters,
            [key]: {
              condition: formData.termFilters[key]?.condition,
              values: formData.termFilters[key]?.values.some((value) => value.i === newValue)
                ? formData.termFilters[key]?.values.filter((value) => value.i !== newValue)
                : [...(formData.termFilters[key]?.values ?? []), { i: newValue }]
            }
          }
        };
        break;
      case FILTER_OPERATION.Delete:
        newFormData = {
          ...formData,
          termFilters: {
            ...formData.termFilters,
            [key]: {
              condition: formData.termFilters[key]?.condition,
              values: formData.termFilters[key].values.filter((value) => value.i !== newValue)
            }
          }
        };
        break;

      case FILTER_OPERATION.Clear_All:
        newFormData = getEmptyFormData({ appName });
        break;
      default:
        newFormData = formData;
        break;
    }

    updateFilterParams(newFormData);
  };

  return { updateSavedSearch, formData };
};
